import { useId, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, ScanLine } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { ScanRefusal } from '@/api/processStepsApi'

/**
 * Scan an equipment tag to verify the unit and hold it — the "all in one BRC
 * click" of z8nrz7c8g4.
 *
 * ONE OF THESE PER REQUIREMENT ROW. The operator scans at the booking they are
 * filling, which is what makes the target BINDING server-side: they are
 * asserting "this machine satisfies THIS requirement", so a wrong-type unit is
 * refused on this row rather than quietly booked against another one. It
 * replaces reading a dropdown by eye, which cannot prove anything about the
 * machine actually in front of them.
 *
 * A WEDGE SCANNER IS A KEYBOARD. It types the code and presses Enter, so this
 * is a plain text input inside a form, with no decoding library and no camera.
 * Manual typing works identically, which is the fallback when a label is
 * damaged.
 *
 * THE VERDICT IS THE SERVER'S. This component never decides whether a unit
 * matches — it submits the raw scan and renders what came back. A client-side
 * check would be defeated by any direct API call, and the match is a regulated
 * gate. There is deliberately no override control: unlike a mis-picked
 * consumable, a wrong machine is not recoverable, so a refusal is final.
 */
export function EquipmentScanField({
  onScan,
  disabled,
  pending,
  refusal,
  lastVerified,
  verifyingHold = false,
  className,
}: {
  /** Submits the raw scanned string. Resolves true when the unit was held. */
  onScan: (scannedCode: string) => Promise<boolean>
  disabled?: boolean
  pending?: boolean
  /**
   * The row already holds this unit, so the scan CONFIRMS it rather than picking
   * it — it stamps the actual start against the planned one. Only the wording
   * changes; the field and the endpoint are the same.
   */
  verifyingHold?: boolean
  /** The server's refusal, or null. Rendered as expected versus scanned. */
  refusal?: ScanRefusal | null
  /** Label of the unit the last successful scan held. */
  lastVerified?: string | null
  className?: string
}) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  // Per-instance, because a step configures several requirements and each one
  // renders this field. A hardcoded id was safe while there was a single field
  // in the tab; now it would repeat across rows and `aria-describedby` would
  // resolve to whichever error happened to mount first.
  const refusalId = `${useId()}-refusal`

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = value.trim()
    if (!code || pending) return
    const held = await onScan(code)
    // Cleared only on success. A refused code stays on screen so the operator
    // can compare what they scanned against what the step expected, instead of
    // watching the field blank itself and having to guess what went in.
    if (held) setValue('')
    // Refocus either way — the next scan should not need a click.
    inputRef.current?.focus()
  }

  return (
    <div className={className}>
      <form onSubmit={submit} className='flex items-center gap-2'>
        <div className='relative min-w-0 flex-1'>
          <ScanLine className='pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400' />
          <Input
            ref={inputRef}
            value={value}
            onChange={e => setValue(e.target.value)}
            disabled={disabled}
            // READ-ONLY WHILE PENDING, NOT DISABLED. Disabling blurs the input
            // for the round trip, so focus lands somewhere else in the modal and
            // the next scan types into whatever caught it. `readOnly` keeps
            // focus here. It also still blocks mutation, which matters: the
            // value is only cleared AFTER the await, so an unguarded input would
            // let a second scan concatenate onto the in-flight code. Keystrokes
            // during the round trip are dropped either way — that is inherent,
            // not something this fixes. `submit` guards re-entrancy separately.
            readOnly={pending}
            placeholder={verifyingHold ? 'Scan to start on this unit…' : 'Scan this unit…'}
            aria-label='Scan equipment label for this requirement'
            aria-invalid={!!refusal}
            aria-describedby={refusal ? refusalId : undefined}
            // Scanners emit fast and the browser's saved-value dropdown would
            // cover the row beneath mid-scan.
            autoComplete='off'
            spellCheck={false}
            className='h-8 pl-7 text-sm'
          />
        </div>
        <Button
          type='submit'
          variant='outline'
          disabled={disabled || pending || !value.trim()}
          className='h-8 shrink-0 px-3 text-xs font-semibold'
        >
          {pending ? 'Checking…' : 'Verify'}
        </Button>
      </form>

      {/* Says what the box is for. Without this it reads as an ordinary text
          input and gives no clue that a hardware scanner types into it, that
          typing works just as well, or that the code is not the unit's name. */}
      {!refusal && !lastVerified && (
        <p className='mt-1.5 pl-1 text-[11px] leading-snug text-gray-500'>
          {verifyingHold ? (
            <>
              Scanning this unit records the actual start against the expected one above.
              Point the scanner at its label, or type its code (
              <span className='font-mono'>EQP-…</span>) and press Enter.
            </>
          ) : (
            <>
              Point the scanner at the label on the machine, or type its code (
              <span className='font-mono'>EQP-…</span>) and press Enter.
            </>
          )}
        </p>
      )}

      {refusal && (
        <div
          id={refusalId}
          role='alert'
          className='mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-800'
        >
          <p className='flex items-start gap-1.5 font-medium'>
            <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600' />
            <span>{refusal.message || 'That scan was refused.'}</span>
          </p>
          {/* Naming both sides is the point — "scan failed" tells an operator
              holding the wrong machine nothing about which one to fetch. */}
          {(refusal.expected || refusal.scanned) && (
            <dl className='mt-1.5 space-y-0.5 pl-5'>
              {refusal.expected && (
                <div className='flex gap-1.5'>
                  <dt className='shrink-0 text-red-700/70'>Expected</dt>
                  <dd className='min-w-0 font-medium'>{refusal.expected}</dd>
                </div>
              )}
              {refusal.scanned && (
                <div className='flex gap-1.5'>
                  <dt className='shrink-0 text-red-700/70'>Scanned</dt>
                  <dd className='min-w-0 font-medium'>{refusal.scanned}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      )}

      {/* `role='status'` because the verification IS the regulated act — a
          screen-reader user must be told it passed, not only that it failed. */}
      {!refusal && lastVerified && (
        <p role='status' className='mt-2 flex items-center gap-1.5 text-xs text-green-700'>
          <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-green-600' />
          Verified and reserved {lastVerified}
        </p>
      )}
    </div>
  )
}
